import { POPULATION_TYPE } from "@/common/models";
import { DataSourceType } from "@/views/customize/data-sources/data/model";
export type SourceAndTimeFrame = {
  type: "session";
  source: DataSourceType;
  name: string;
  time: string;
};
export type SourceAndSession = {
  type: "time_frame";
  source: DataSourceType;
  time: string;
};
export type SourceAndTime = SourceAndTimeFrame | SourceAndSession;
export type CognniReport = {
  id: string;
  name: string;
  generated_by: {
    id: string;
    email: string;
  };
  template: string;
  date: string;
  // need to hold this because maybe the template
  // was changed after this date.
  findings: ExecutedReportTemplateSection[];
};

export type ReportTemplateSection = {
  finding: string;
  question: string;
  source: DataSourceType;
  label: string;
};

export type ExecutedReportTemplateSection = ReportTemplateSection & {
  time: string;
  // when there is only one : [{count:20,name:"default"}]
  // when there is more than one : [{count:20,name:"high"},{count:20,name:"medium"},{count:10, name:"low"}]
  counters: Array<{ count: number; name: string }>;
};

export type ReportTemplate = {
  id: string;
  created_at: string;
  created_by: string;
  name: string;
  sections: ReportTemplateSection[];
};

export type CognniCsvReportParamType =
  | "date"
  | "severity"
  | "sensitivity"
  | "application"
  | "finding";

export type CsvReportGqlInput = {
  name: string;
  type: "GraphQL";
  graphql: {
    map: Array<Record<string, string>>;
    query: string;
  };
  tags: string[];
};

export type CsvReportFindingInput = {
  name: string;
  type: "Findings";
  tags: string[];
  query: string;
  map: Array<Record<string, string>>;
};

export type CsvReportInput = CsvReportGqlInput | CsvReportFindingInput;
export type CsvReportStatus = "New" | "InProgress" | "Ready" | "Failed";
export type CsvReportType = "Findings" | "GraphQL";

export type CsvReportScheduleInput = {
  subscribers: string[];
  timeFrame: "weekly" | "monthly";
  description?: string;
  name: string;
  query: Omit<CsvReportGqlInput, "name">;
};

export type CsvScheduleItem = {
  ruleId: string;
  subscribers: string[];
  timeFrame: "weekly" | "monthly";
  name: string;
  description: string;
};

const is_cognni_admin = (user_id: string) => {
  return user_id === "00000000-0000-0000-0000-00000000000f";
};

export type CognniCsvReport = {
  id: string;
  name: string;
  status: CsvReportStatus;
  status_str: string;
  created_at: string;
  type: CsvReportType;
  generated_by: {
    id: string;
    email: string;
  };
  entity_type: string;
  params: Array<{
    value: string;
    type: CognniCsvReportParamType;
  }>;
  entities_count: number;
};

export const CognniCsvReportHelper = {
  to_status_display: (s: CsvReportStatus): string => {
    switch (s) {
      case "New":
        return "New";
      case "InProgress":
        return "In Progress";
      case "Ready":
        return "Ready";
      case "Failed":
        return "Failed";
    }
  },
  from_api_item: (f: any): CognniCsvReport => {
    const s = (f.tags || []).filter((t: string) =>
      t.startsWith("entity_type:")
    );
    const params = (f.tags || [])
      .map((t: string) => {
        const [key, value] = t.split(":");
        return {
          type: key,
          value,
        };
      })
      .filter((p: any) => p.type !== "entity_type");
    return {
      created_at: f.createdAt,
      id: f.id,
      name: f.name,
      type: f.type,
      status: f.status,
      entity_type: s.length > 0 ? s[0].split(":")[1] : POPULATION_TYPE.EVENT,
      status_str: CognniCsvReportHelper.to_status_display(f.status),
      params,
      entities_count: f.size,
      generated_by: {
        id: f.userId,
        email: f.userEmail
          ? f.userEmail
          : is_cognni_admin(f.userId)
          ? "Cognni"
          : "--",
      },
    };
  },
};
